import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkIn", "checkOut", "preview", "nights", "basePrice", "fees", "tax", "total", "difference", "form"]
  static values = {
    reservationId: Number,
    pricingSnapshot: Object,
    currentTotal: Number
  }

  connect() {
    console.log("Modification pricing controller connected")
    this.updatePricing()
  }

  updatePricing(event) {
    if (event) {
      event.preventDefault()
    }

    // Get date values
    const checkInValue = this.hasCheckInTarget ? this.checkInTarget.value : null
    const checkOutValue = this.hasCheckOutTarget ? this.checkOutTarget.value : null

    if (!checkInValue || !checkOutValue) {
      this.clearPreview()
      return
    }

    // Parse dates
    const checkIn = this.parseDate(checkInValue)
    const checkOut = this.parseDate(checkOutValue)

    if (!checkIn || !checkOut || checkOut <= checkIn) {
      this.showError('Check-out date must be after check-in date')
      return
    }

    // Calculate nights
    const nights = this.calculateNights(checkIn, checkOut)

    // Fetch pricing from server
    this.fetchPricing(checkIn, checkOut, nights)
  }

  parseDate(dateString) {
    if (!dateString) return null

    try {
      // Try parsing as ISO date string (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number)
        return new Date(year, month - 1, day)
      }

      // Try parsing as MM/DD/YYYY
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
        const [month, day, year] = dateString.split('/').map(Number)
        return new Date(year, month - 1, day)
      }

      console.error(`Failed to parse date: ${dateString}`)
      return null
    } catch (e) {
      console.error(`Error parsing date ${dateString}:`, e)
      return null
    }
  }

  calculateNights(checkIn, checkOut) {
    const diffTime = Math.abs(checkOut - checkIn)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  formatDate(date) {
    if (!date) return ''

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  async fetchPricing(checkIn, checkOut, nights) {
    if (!this.reservationIdValue) {
      console.error('No reservation ID available')
      return
    }

    const url = `/admin/reservations/${this.reservationIdValue}/modification/pricing_preview`
    const params = new URLSearchParams({
      new_check_in: this.formatDate(checkIn),
      new_check_out: this.formatDate(checkOut)
    })

    try {
      const response = await fetch(`${url}?${params}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      this.displayPricing(data, nights)
    } catch (error) {
      console.error('Error fetching pricing:', error)
      this.showError('Failed to calculate pricing. Please try again.')
    }
  }

  displayPricing(data, nights) {
    if (!this.hasPreviewTarget) return

    const priceDifference = data.new_total - this.currentTotalValue
    const isCharge = priceDifference > 0
    const isRefund = priceDifference < 0

    this.previewTarget.innerHTML = `
      <div class="bg-gray-50 rounded-lg p-4 mt-4">
        <h4 class="font-semibold text-gray-900 mb-3">Pricing Preview</h4>

        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Nights:</span>
            <span class="font-medium text-gray-900">${nights}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-gray-600">Base Price:</span>
            <span class="text-gray-900">${this.formatCurrency(data.base_price)}</span>
          </div>

          ${data.cleaning_fee > 0 ? `
            <div class="flex justify-between">
              <span class="text-gray-600">Cleaning Fee:</span>
              <span class="text-gray-900">${this.formatCurrency(data.cleaning_fee)}</span>
            </div>
          ` : ''}

          <div class="flex justify-between">
            <span class="text-gray-600">Subtotal:</span>
            <span class="text-gray-900">${this.formatCurrency(data.subtotal)}</span>
          </div>

          <div class="flex justify-between">
            <span class="text-gray-600">Tax:</span>
            <span class="text-gray-900">${this.formatCurrency(data.tax)}</span>
          </div>

          <div class="flex justify-between pt-2 border-t border-gray-300">
            <span class="font-semibold text-gray-900">New Total:</span>
            <span class="font-semibold text-gray-900">${this.formatCurrency(data.new_total)}</span>
          </div>

          <div class="flex justify-between pt-2 border-t-2 border-gray-400">
            <span class="font-semibold text-gray-900">Price Difference:</span>
            <span class="font-semibold ${isCharge ? 'text-red-600' : (isRefund ? 'text-green-600' : 'text-gray-900')}">
              ${priceDifference > 0 ? '+' : ''}${this.formatCurrency(priceDifference)}
            </span>
          </div>
        </div>

        ${this.getAdjustmentMessage(priceDifference)}
      </div>
    `
  }

  getAdjustmentMessage(difference) {
    if (difference > 0) {
      return `
        <div class="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p class="text-sm text-amber-900">
            <i class="ph ph-credit-card"></i>
            <strong>Additional charge:</strong> Customer will be charged ${this.formatCurrency(difference)}
          </p>
        </div>
      `
    } else if (difference < 0) {
      return `
        <div class="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
          <p class="text-sm text-green-900">
            <i class="ph ph-arrow-counter-clockwise"></i>
            <strong>Refund:</strong> Customer will receive ${this.formatCurrency(Math.abs(difference))} refund
          </p>
        </div>
      `
    } else {
      return `
        <div class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p class="text-sm text-gray-700">
            <i class="ph ph-check-circle"></i>
            <strong>No price change</strong> - Same total with new dates
          </p>
        </div>
      `
    }
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  clearPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }
  }

  showError(message) {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = `
        <div class="mt-4 rounded-md bg-red-50 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">${message}</p>
            </div>
          </div>
        </div>
      `
    }
  }
}
